import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiTruck, FiStar, FiUserCheck, FiBriefcase, FiTrendingUp, FiAward } from 'react-icons/fi';
import { getLeaderboard } from '../services/performance';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import toast from 'react-hot-toast';

const typeConfig = {
  drivers: {
    title: 'Top Drivers',
    icon: FiTruck,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    stats: [
      { key: 'total_revenue', label: 'Revenue', prefix: '$' },
      { key: 'completed_orders', label: 'Orders', prefix: '' },
      { key: 'average_rating', label: 'Rating', prefix: '' }
    ]
  },
  tour_guides: {
    title: 'Top Tour Guides',
    icon: FiUserCheck,
    color: 'text-purple-500',
    bgColor: 'bg-purple-50',
    stats: [
      { key: 'total_revenue', label: 'Revenue', prefix: '$' },
      { key: 'completed_orders', label: 'Tours', prefix: '' },
      { key: 'average_rating', label: 'Rating', prefix: '' }
    ]
  },
  tour_companies: {
    title: 'Top Tour Companies',
    icon: FiBriefcase,
    color: 'text-green-500',
    bgColor: 'bg-green-50',
    stats: [
      { key: 'total_revenue', label: 'Revenue', prefix: '$' },
      { key: 'completed_orders', label: 'Bookings', prefix: '' }
    ]
  }
};

export default function Leaderboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('drivers');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('total_revenue');

  useEffect(() => {
    fetchLeaderboard();
  }, [activeTab, sortBy]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await getLeaderboard(activeTab, sortBy);
      setData(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getValue = (item, key) => {
    switch (key) {
      case 'total_revenue':
        return item.finances?.total_earned || item.stats?.total_revenue || 0;
      case 'completed_orders':
        return item.stats?.completed_orders || 0;
      case 'average_rating':
        return item.rating?.average || 0;
      default:
        return 0;
    }
  };

  const formatValue = (value, key, prefix = '') => {
    if (key === 'average_rating') {
      return value.toFixed(1);
    }
    return `${prefix}${value.toLocaleString()}`;
  };

  const config = typeConfig[activeTab];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Performance Leaderboard</h1>
        <div className="flex space-x-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="total_revenue">Sort by Revenue</option>
            <option value="completed_orders">Sort by Orders</option>
            <option value="average_rating">Sort by Rating</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6 border-b">
        {Object.entries(typeConfig).map(([key, cfg]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center px-6 py-3 font-medium transition ${
              activeTab === key
                ? 'border-b-2 border-primary-500 text-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <cfg.icon className="w-5 h-5 mr-2" />
            {cfg.title}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : data.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center text-gray-500">
          No data available yet
        </div>
      ) : (
        <div className="space-y-4">
          {data.map((item, index) => (
            <div
              key={item._id}
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition cursor-pointer"
              onClick={() => navigate(`/${activeTab}/${item._id}/stats`)}
            >
              <div className="p-5 flex items-center justify-between">
                {/* Rank */}
                <div className="flex items-center w-16">
                  {index === 0 ? (
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                      <FiAward className="w-6 h-6 text-yellow-600" />
                    </div>
                  ) : index === 1 ? (
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-lg font-bold text-gray-500">2</span>
                    </div>
                  ) : index === 2 ? (
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-lg font-bold text-orange-600">3</span>
                    </div>
                  ) : (
                    <span className="w-10 text-center text-lg font-medium text-gray-400">
                      {index + 1}
                    </span>
                  )}
                </div>

                {/* Avatar/Icon */}
                <div className={`w-12 h-12 ${config.bgColor} rounded-full flex items-center justify-center`}>
                  <config.icon className={`w-6 h-6 ${config.color}`} />
                </div>

                {/* Info */}
                <div className="flex-1 ml-4">
                  <h3 className="font-semibold text-gray-800">{item.name}</h3>
                  {item.phone && (
                    <p className="text-xs text-gray-500">{item.phone}</p>
                  )}
                  {activeTab === 'tour_guides' && item.languages && (
                    <div className="flex gap-1 mt-1">
                      {item.languages.slice(0, 3).map((lang, idx) => (
                        <span key={idx} className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                          {lang.code.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="flex space-x-8">
                  {config.stats.map((stat) => (
                    <div key={stat.key} className="text-right">
                      <p className="text-xs text-gray-500">{stat.label}</p>
                      <p className="text-lg font-bold text-gray-800">
                        {formatValue(getValue(item, stat.key), stat.key, stat.prefix)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
